import * as React from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    Table as ReactTable,
} from '@tanstack/react-table';
import { ChevronDown, MoreHorizontal } from 'lucide-react';

import { Button } from '@shadcn/ui/components/ui/button';
import { Checkbox } from '@shadcn/ui/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@shadcn/ui/components/ui/dropdown-menu';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@shadcn/ui/components/ui/pagination';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/ui/components/ui/table';
import { GET_USER_CONTACT_IDS_BY_PROSPECT_IDS, LIST_CONTACTS, Order_By } from '@zaplify/graphql';
import { useQuery as useApolloQuery } from '@apollo/client';
import { PlaybookBadge } from '../../../components/playbook-badge';
import { Suspense, useMemo, useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { cn } from '@shadcn/ui/lib/utils';
import { AccountLogo } from '../../../components/account-logo';
import { ContactAvatar } from '../../../components/contact-avatar';
import { ContactsTableToolBar } from './contacts-table-toolbar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/ui/components/ui/select';

import { useAccountDetailsDialog } from '../../../components/dialogs/account-details-dialog';
import { TextSearchHighlighted } from '../../../components/text-search-highlighted';
import { paths } from '../../../../routes/paths';
import { useActions } from '../../../hooks/use-actions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { motion } from 'motion/react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { ContactTypeBadge } from '../../../components/contact-type-badge';

type Contact = {
    id: string;
    prospectId: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    location?: string;
    occupationTitle?: string;
    organizationName?: string;
    organizationDomain?: string;
    organizationIndustry?: string;
    organizationSize?: string;
    linkedinProfileImgUrl?: string;
    linkedinHeadline?: string;
    accountId?: string;
    createdAt: string;
    connectedAt?: string;
    metadata?: { contactType?: string };
    _GroupToContacts: Array<{
        Group: {
            id: string;
            name: string;
            imgSrc?: string;
            default?: boolean;
            assistantSettingsId?: string;
            createdAt: string;
            updatedAt: string;
        };
    }>;
};

type RowProps = {
    contact: Contact;
    userContactId: string;
};
export type ContactsTable = ReactTable<RowProps>;

// Column minimum widths mapping - using wider defaults to allow expansion
const CUSTOM_COLUMN_WIDTHS: Record<string, string> = {
    industry: 'max-w-56 w-auto',
    companySize: 'min-w-16 w-auto',
    location: 'max-w-96 w-auto',
} as const;

const getColumnMinWidth = (columnId: string): string => {
    return CUSTOM_COLUMN_WIDTHS[columnId] || 'max-w-64 w-auto';
};

const ContactCell = ({ row, filterValue }: { row: RowProps; filterValue?: string }) => {
    const { contactId } = useParams();
    const isSelected = contactId === row.contact.id;
    return (
        <Link
            to={isSelected ? '.' : `./contact/${row.contact.id}`}
            className={cn('flex items-center gap-2', isSelected && 'font-semibold')}
            state={{ from: location.pathname }}
        >
            {Boolean(row.contact?.linkedinHeadline && !row.contact?.organizationName) ? (
                <>
                    {/* Subtle spinning border - positioned outside the avatar */}
                    <Tooltip>
                        <>
                            <TooltipTrigger asChild>
                                <div className="relative">
                                    <ContactAvatar
                                        avatarUrl={row.contact.linkedinProfileImgUrl}
                                        fullName={row.contact.fullName}
                                        className="relative"
                                    />
                                    <motion.div
                                        className="absolute inset-0 border-2 border-transparent border-t-muted-foreground rounded-full"
                                        animate={{ rotate: 360 }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: 'linear',
                                        }}
                                    />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                We're still syncing your network and gathering more information about this contact...
                            </TooltipContent>
                        </>
                    </Tooltip>
                </>
            ) : (
                <ContactAvatar avatarUrl={row.contact.linkedinProfileImgUrl} fullName={row.contact.fullName} />
            )}
            <TextSearchHighlighted text={row.contact.fullName || ''} value={filterValue || ''} className="capitalize" />
        </Link>
    );
};

export const createColumns = (
    onShowAccount: (accountId: string) => void,
    onCreateActionNow?: (prospectId: string) => void,
    showContactType?: boolean
): ColumnDef<RowProps>[] => [
    {
        id: 'select',
        header: ({ table }) => (
            <div className="px-2">
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected()
                            ? true
                            : table.getIsSomePageRowsSelected()
                            ? 'indeterminate'
                            : false
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            </div>
        ),
        cell: ({ row }) => (
            <div className="px-2">
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            </div>
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        id: 'contact',
        header: 'Contact',
        accessorFn: (row) => row.contact.fullName || '',
        cell: ({ row, table }) => {
            const filterValue = table.getColumn('contact')?.getFilterValue() as string;
            return <ContactCell row={row.original} filterValue={filterValue} />;
        },
    },
    ...(showContactType
        ? [
              {
                  id: 'contactType',
                  header: 'Contact type',
                  accessorFn: (row) => row.contact.metadata?.contactType,
                  cell: ({ row }) => {
                      return (
                          <ContactTypeBadge
                              contactId={row.original.contact.id}
                              contactType={row.original.contact.metadata?.contactType}
                          />
                      );
                  },
              },
          ]
        : []),
    {
        id: 'jobTitle',
        header: 'Job title',
        accessorFn: (row) => row.contact.occupationTitle || row.contact.linkedinHeadline,
        cell: ({ row, table }) => {
            const filterValue = table.getColumn('contact')?.getFilterValue() as string;
            return (
                <div>
                    <TextSearchHighlighted
                        text={row.original.contact.occupationTitle || row.original.contact.linkedinHeadline}
                        value={filterValue || ''}
                        className="line-clamp-2"
                    />
                </div>
            );
        },
    },
    {
        id: 'company',
        header: 'Company',
        accessorFn: (row) => row.contact.organizationName,
        cell: ({ row, table }) => {
            const filterValue = table.getColumn('contact')?.getFilterValue() as string;
            return (
                <div className="flex items-center gap-2">
                    <Button
                        variant="link"
                        className={cn(
                            'font-normal px-0',
                            !row.original.contact.accountId && 'hover:cursor-default hover:no-underline'
                        )}
                        onClick={() =>
                            row.original.contact.accountId ? onShowAccount(row.original.contact.accountId) : null
                        }
                    >
                        <AccountLogo accountWebsite={row.original.contact.organizationDomain} className="w-8 h-8" />
                        <TextSearchHighlighted
                            text={row.original.contact.organizationName || ''}
                            value={filterValue || ''}
                            className="truncate max-w-48"
                        />
                    </Button>
                </div>
            );
        },
    },
    {
        id: 'playbook',
        header: 'Playbook',
        accessorFn: (row) => row.contact._GroupToContacts?.[0]?.Group?.name,
        cell: ({ row }) => (
            <Suspense
                fallback={
                    <div className="flex items-center gap-2">
                        {row.original.contact._GroupToContacts?.[0]?.Group?.imgSrc && (
                            <img
                                src={row.original.contact._GroupToContacts?.[0]?.Group?.imgSrc}
                                alt={row.original.contact._GroupToContacts?.[0]?.Group?.name}
                                className="h-6 w-6 rounded-full"
                            />
                        )}
                        <span>{row.original.contact._GroupToContacts?.[0]?.Group?.name}</span>
                    </div>
                }
            >
                <PlaybookBadge
                    prospectId={row.original.contact.prospectId}
                    responsive={false}
                    size="small"
                    className="min-w-[170px]"
                />
            </Suspense>
        ),
    },
    {
        id: 'industry',
        header: 'Industry',
        accessorFn: (row) => row.contact.organizationIndustry,
        cell: ({ row }) => (
            <div className={cn({ 'text-muted-foreground': !row.original.contact.organizationIndustry })}>
                {row.original.contact.organizationIndustry ?? 'Unknown'}
            </div>
        ),
    },
    {
        id: 'companySize',
        header: 'Company size',
        accessorFn: (row) => row.contact.organizationSize,
        cell: ({ row }) => (
            <div className={cn({ 'text-muted-foreground': !row.original.contact.organizationSize })}>
                {row.original.contact.organizationSize?.replace(/[^\d- ]/g, '') ?? 'Unknown'}
            </div>
        ),
    },
    {
        id: 'addedOn',
        header: 'Added On',
        enableHiding: true,
        accessorFn: (row) => row.contact.createdAt,
        cell: ({ row }) => {
            const createdAt = new Date(row.original.contact.createdAt);
            return <div>{createdAt.toLocaleDateString()}</div>;
        },
    },
    {
        id: 'connectedAt',
        header: 'Connected On',
        enableHiding: true,
        accessorFn: (row) => row.contact.connectedAt,
        cell: ({ row }) => {
            const connectedAt = row.original.contact.connectedAt;
            if (!connectedAt) return <div className="text-muted-foreground">-</div>;
            const date = new Date(connectedAt);
            return <div>{date.toLocaleDateString()}</div>;
        },
    },
    {
        id: 'location',
        header: 'Location',
        accessorFn: (row) => row.contact.location,
        cell: ({ row, table }) => {
            return <div>{row.original.contact.location}</div>;
        },
    },
    {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) => {
            const contact = row.original.contact;

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                            <Link to={`./contact/${contact.id}`}>View Details</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            disabled={!row.original.contact.accountId}
                            onClick={() => onShowAccount(row.original.contact.accountId)}
                        >
                            View Account
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                            <Link to={`${paths.NEW.MESSAGES}/contact/${contact.id}`}>Go to Conversation</Link>
                        </DropdownMenuItem>
                        {onCreateActionNow && (
                            <DropdownMenuItem
                                onClick={() => {
                                    onCreateActionNow(contact.prospectId);
                                }}
                            >
                                Create Action Now
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        },
    },
];

export const ContactsTable = () => {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
        addedOn: false,
    });
    const [rowSelection, setRowSelection] = React.useState({});
    const { openAccountDetailsDialog } = useAccountDetailsDialog();
    const { handleSnooze } = useActions();
    const { contactId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamGroupId = searchParams.get('groupId');
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25,
    });

    const { actionsV2: actionsV2Enabled, showContactTypeInContactsTable: showContactType } = useFlags();

    const onCreateActionNow = useMemo(
        () => async (prospectId: string) => {
            await handleSnooze(prospectId, 0);
        },
        [handleSnooze]
    );

    const columns = useMemo(
        () =>
            createColumns(openAccountDetailsDialog, actionsV2Enabled ? undefined : onCreateActionNow, showContactType),
        []
    );
    const [rows, setRows] = useState<RowProps[]>([]);

    // Initialize with basic search from URL params
    const [searchTerm, setSearchTerm] = useState('');

    // Build where clause for the query
    const whereClause = useMemo(() => {
        const conditions: any[] = [];

        // Add search filter if provided
        if (searchTerm) {
            conditions.push({
                _or: [
                    { fullName: { _ilike: `%${searchTerm}%` } },
                    { organizationName: { _ilike: `%${searchTerm}%` } },
                    { occupationTitle: { _ilike: `%${searchTerm}%` } },
                ],
            });
        }

        // Add playbook filter if provided
        if (searchParamGroupId) {
            conditions.push({
                _GroupToContacts: {
                    Group: { id: { _eq: searchParamGroupId } },
                },
            });
        }

        return conditions.length > 0 ? { _and: conditions } : {};
    }, [searchTerm, searchParamGroupId]);

    const { loading: isContactsLoading, data: contactsData } = useApolloQuery(LIST_CONTACTS, {
        variables: {
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
            orderBy: { connectedAt: Order_By.DescNullsLast },
            where: whereClause,
        },
        fetchPolicy: 'network-only',
    });

    const prospectIds = useMemo(() => {
        return contactsData?.Contacts.map((contact) => contact.prospectId) || [];
    }, [contactsData]);

    const { data: userContactIds } = useApolloQuery(GET_USER_CONTACT_IDS_BY_PROSPECT_IDS, {
        variables: { prospectIds: prospectIds },
        skip: !prospectIds?.length,
    });

    const table = useReactTable({
        data: rows,
        pageCount: Math.ceil((contactsData?.Contacts_aggregate?.aggregate?.count || 0) / pagination.pageSize),
        columns,
        initialState: {
            pagination,
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        onPaginationChange: setPagination,
        manualPagination: true,
        debugTable: false,
        state: {
            pagination,
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    });

    // Sync search term with table filter
    useEffect(() => {
        const filterValue = table.getColumn('contact')?.getFilterValue() as string;
        setSearchTerm(filterValue || '');
    }, [table.getColumn('contact')?.getFilterValue()]);

    const startRowIndex = pagination.pageIndex * pagination.pageSize;

    useEffect(() => {
        table.resetRowSelection();
    }, [contactsData]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, [searchTerm]);

    const hasPreviousPage = useMemo(() => {
        return startRowIndex > 0;
    }, [pagination.pageIndex, pagination.pageSize]);
    const hasNextPage = useMemo(() => {
        return startRowIndex + pagination.pageSize < (contactsData?.Contacts_aggregate?.aggregate?.count || 0);
    }, [contactsData, pagination.pageIndex, pagination.pageSize]);

    useEffect(() => {
        if (contactsData?.Contacts) {
            const newRows = contactsData.Contacts.map((contact) => ({
                contact,
                userContactId: userContactIds?.UserContacts?.find(
                    (userContact) => userContact?.prospectId === contact?.prospectId
                )?.id,
            }));
            setRows(newRows);
        } else {
            setRows([]);
        }
    }, [contactsData, searchTerm, userContactIds]);

    const isLoading = isContactsLoading;

    return (
        <div className="w-full flex flex-col h-full">
            <ContactsTableToolBar table={table} />
            <div className="flex-1 min-h-0">
                <div className="rounded-2xl border bg-white h-full overflow-auto">
                    <div className="w-max min-w-full">
                        <Table className="border-separate border-spacing-0 w-full">
                            <TableHeader className="bg-gray-100 sticky top-0 z-10">
                                {table.getHeaderGroups().map((headerGroup, index) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const isFirstHeader = header === headerGroup.headers[0];
                                            const isLastHeader =
                                                header === headerGroup.headers[headerGroup.headers.length - 1];

                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    className={cn(
                                                        getColumnMinWidth(header.column.id),
                                                        'py-3 border-b-1 border-gray-200 bg-gray-100',
                                                        isFirstHeader && 'rounded-tl-2xl',
                                                        isLastHeader && 'rounded-tr-2xl'
                                                    )}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext()
                                                          )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: pagination.pageSize }).map((_, index) => (
                                        <TableRow key={index}>
                                            {columns.map((column, colIndex) => (
                                                <TableCell
                                                    key={colIndex}
                                                    className={getColumnMinWidth(column.id || '')}
                                                >
                                                    <Skeleton className="h-11 w-full" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row, rowIndex) => (
                                        <TableRow
                                            key={row.id}
                                            data-state={row.getIsSelected() && 'selected'}
                                            className={cn(
                                                row.getIsSelected() && 'bg-gray-100',
                                                row?.original?.contact?.id === contactId && 'bg-gray-100'
                                            )}
                                        >
                                            {row.getVisibleCells().map((cell, cellIndex) => (
                                                <TableCell
                                                    key={cell.id}
                                                    className={cn('border-b', getColumnMinWidth(cell.column.id))}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            No results.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of {table.getFilteredRowModel().rows.length}{' '}
                    row(s) selected.
                </div>
                <div className="flex items-center gap-2">
                    <Select
                        value={String(pagination.pageSize)}
                        onValueChange={(value) => setPagination((prev) => ({ ...prev, pageSize: Number(value) }))}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder={`Show ${pagination.pageSize}`} />
                        </SelectTrigger>
                        <SelectContent>
                            {[10, 25, 50, 100].map((size) => (
                                <SelectItem key={size} value={String(size)}>
                                    {size}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Pagination>
                        <PaginationContent className="pr-12">
                            {hasPreviousPage && (
                                <PaginationItem>
                                    <PaginationPrevious
                                        onClick={() => table.previousPage()}
                                        isActive={hasPreviousPage}
                                        className={cn(!hasPreviousPage && 'hover:bg-primary')}
                                    />
                                </PaginationItem>
                            )}
                            <PaginationItem>
                                <PaginationLink className="hover:bg-primary border-none" isActive>
                                    {pagination.pageIndex + 1}
                                </PaginationLink>
                            </PaginationItem>
                            {(hasNextPage || isContactsLoading) && (
                                <PaginationItem>
                                    <PaginationNext
                                        onClick={() => table.nextPage()}
                                        isActive={hasNextPage && !isContactsLoading}
                                        className={cn(!hasNextPage && 'hover:bg-primary')}
                                    />
                                </PaginationItem>
                            )}
                        </PaginationContent>
                    </Pagination>
                </div>
            </div>
        </div>
    );
};

export default ContactsTable;
